/** What a date column or field shows when it has no date. */
export const NO_DATE = '—';

/**
 * Converts an ISO date string to YYYY-MM-DD format for HTML date inputs
 * @param isoString - ISO date string like "2025-08-01T00:00:00.000Z"
 * @returns Date string in YYYY-MM-DD format or empty string if invalid
 */
export function isoToDateInput(isoString: string | null | undefined): string {
  if (!isoString) return '';

  try {
    const date = new Date(isoString);
    if (isNaN(date.getTime())) return '';

    // Format as YYYY-MM-DD
    return date.toISOString().split('T')[0]!;
  } catch {
    return '';
  }
}

/**
 * Formats an ISO date string for display
 * @param isoString - ISO date string
 * @param options - Intl.DateTimeFormat options
 * @returns Formatted date string or NO_DATE if empty or invalid
 */
export function formatDate(
  isoString: string | null | undefined,
  options?: Intl.DateTimeFormatOptions,
): string {
  if (!isoString) return NO_DATE;

  try {
    const date = new Date(isoString);
    if (isNaN(date.getTime())) return NO_DATE;

    return date.toLocaleDateString(undefined, options);
  } catch {
    return NO_DATE;
  }
}

/**
 * Formats a date-only string ('YYYY-MM-DD') for display. Parses the parts
 * locally — `new Date('YYYY-MM-DD')` is UTC midnight and would render the
 * previous day for viewers west of UTC.
 * @param isoDate - date-only string ('YYYY-MM-DD')
 * @returns Formatted date string or NO_DATE if empty or invalid
 */
export function formatDateOnly(isoDate: string | null | undefined): string {
  if (!isoDate) return NO_DATE;

  const [year, month, day] = isoDate.split('-').map(Number);
  if (!year || !month || !day) return NO_DATE;

  return new Date(year, month - 1, day).toLocaleDateString();
}

/**
 * Formats an ISO date string for display with time
 * @param isoString - ISO date string
 * @param options - Intl.DateTimeFormat options
 * @returns Formatted datetime string or NO_DATE if empty or invalid
 */
export function formatDateTime(
  isoString: string | null | undefined,
  options?: Intl.DateTimeFormatOptions,
): string {
  if (!isoString) return NO_DATE;

  try {
    const date = new Date(isoString);
    if (isNaN(date.getTime())) return NO_DATE;

    return date.toLocaleString(undefined, options);
  } catch {
    return NO_DATE;
  }
}

/**
 * Formats a date as a compact relative time ("just now", "5m ago", "3h ago",
 * "2d ago"), falling back to an absolute date for anything older than a week.
 */
export function formatRelativeTime(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffSecs = Math.floor(diffMs / 1000);
  const diffMins = Math.floor(diffSecs / 60);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffSecs < 60) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

/**
 * Formats a notification time (HH:MM:SS format) for display
 * @param notificationTime - Time string in HH:MM:SS format (already in user's timezone)
 * @returns Formatted time string in HH:MM format or 'N/A' if invalid
 */
export function formatNotificationTime(
  notificationTime: string | null | undefined,
): string {
  if (!notificationTime) return 'N/A';

  try {
    const [hours, minutes] = notificationTime.split(':');
    return `${hours}:${minutes}`;
  } catch {
    return 'N/A';
  }
}
